import { useDebouncedCallback } from '@mantine/hooks';
import { Box, LinearProgress, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  DataGridPro,
  DataGridProProps,
  GridCell,
  GridCellProps,
  GridColDef,
  GridRow,
  GridRowProps,
  GridSlots,
} from '@mui/x-data-grid-pro';
import classNames from 'classnames';
import { getDatagridStylesInfo } from 'components/Search/styles';
import { CoreLogEntry } from 'providers/contexts/extensions/CoreLogs';
import { forwardRef, memo, useCallback, useMemo, useState } from 'react';
import { LogDetailsModal } from './LogDetailsModal';
import { getLogViewerColumns } from './LogViewerColumns';

/**
 * Log Viewer (Datagrid)
 * Modal version to view logs in a DataGridPro table.
 * Clicking a row opens the details modal for the full message and metadata.
 */

export type LogViewerStatus = 'success' | 'failed' | 'warning' | 'info' | 'unknown';

export type LogViewerRow = {
  /** Stable id for DataGrid row keying. */
  id: string;
  /** The driver / source / target name (legacy "Driver" column). */
  driver: string;
  status: LogViewerStatus;
  /** ISO date-time of the entry. */
  time?: string | null;
  /** Message text. */
  message?: string;
  /** The original log entry object, if the caller wants to use it. */
  item?: CoreLogEntry;
};

export type LogViewerLabels = {
  columnTime: string;
  columnStatus: string;
  columnDriver: string;
  columnMessage: string;
  statusSuccess: string;
  statusFailed: string;
  statusWarning: string;
  statusInfo: string;
  statusUnknown: string;
  empty: string;
  loadingMore: string;
};

export type LogViewerProps = {
  rows: LogViewerRow[];
  isLoading: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  siteTimeZone?: string;
  labels: LogViewerLabels;
  showDriver?: boolean;
  dataTestId?: string;
};

export type LogViewerColDef = GridColDef<LogViewerRow>;
export type LogViewerColDefs = LogViewerColDef[];
type GetRowIdFn = NonNullable<DataGridProProps<LogViewerRow>['getRowId']>;
type GetRowHeightFn = NonNullable<DataGridProProps['getRowHeight']>;
type GetRowClassnameFn = NonNullable<DataGridProProps<LogViewerRow>['getRowClassName']>;
type RowsScrollEndFn = NonNullable<DataGridProProps['onRowsScrollEnd']>;
type RowClickFn = NonNullable<DataGridProProps<LogViewerRow>['onRowClick']>;
type GridSlotsType = Partial<DataGridProProps['slots']>;
type GridSlotPropsType = Partial<DataGridProProps['slotProps']>;
type GridLocaleTextType = NonNullable<DataGridProProps['localeText']>;

export const LogViewer = memo(
  ({
    rows,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    siteTimeZone,
    labels,
    showDriver = true,
    dataTestId = 'log-viewer',
  }: LogViewerProps) => {
    const theme = useTheme();
    const failedBg = alpha(theme.palette.error.main, 0.08);

    // Clicking a row opens the read-only details modal for the full message
    const [detailsRow, setDetailsRow] = useState<LogViewerRow | null>(null);

    // General same styles as other primary search views
    const { sxStyles, rowHeight } = getDatagridStylesInfo({ theme, view: 'list' });

    // Column-def getter
    const columns = useMemo<LogViewerColDefs>(() => {
      return getLogViewerColumns({ labels, siteTimeZone, showDriver });
    }, [labels, siteTimeZone, showDriver]);

    const showLoader = isLoading || isFetchingNextPage;

    // Debounce load-more to help prevent double fetches
    const debouncedFetchNextPage = useDebouncedCallback(() => {
      if (!fetchNextPage) {
        return;
      }
      fetchNextPage();
    }, 300);

    const handleGetRowId = useCallback<GetRowIdFn>((row) => row.id, []);

    const handleGetRowHeight = useCallback<GetRowHeightFn>(() => rowHeight, [rowHeight]);

    const handleGetRowClassName = useCallback<GetRowClassnameFn>((params) => {
      return classNames({
        even: params.indexRelativeToCurrentPage % 2 === 0,
        odd: params.indexRelativeToCurrentPage % 2 !== 0,
        'log-row-failed': params.row.status === 'failed',
      });
    }, []);

    const handleOnRowsScrollEnd = useCallback<RowsScrollEndFn>(() => {
      if (!hasNextPage || isFetchingNextPage || !fetchNextPage) {
        return;
      }
      debouncedFetchNextPage();
    }, [debouncedFetchNextPage, hasNextPage, isFetchingNextPage, fetchNextPage]);

    const handleOnRowClick = useCallback<RowClickFn>((params) => {
      setDetailsRow(params.row);
    }, []);

    const gridSlots: GridSlotsType = useMemo(() => {
      return {
        cell: LogViewerGridCell,
        row: LogViewerGridRow,
        loadingOverlay: LinearProgress as GridSlots['loadingOverlay'],
      } as GridSlotsType;
    }, []);

    const gridSlotProps: GridSlotPropsType = useMemo(() => {
      return {
        row: {} as Partial<LogViewerGridRowProps>,
        cell: { customProps: {} } as Partial<LogViewerGridCellProps>,
      };
    }, []);

    const gridSx = useMemo(() => {
      return {
        ...sxStyles,
        '& .log-row-failed': {
          bgcolor: failedBg,
        },
        '& .MuiDataGrid-row': {
          cursor: 'pointer',
        },
      };
    }, [sxStyles, failedBg]);

    const gridLocaleText: GridLocaleTextType = useMemo(() => {
      return { noRowsLabel: labels.empty };
    }, [labels.empty]);

    return (
      <Box
        data-testid={dataTestId}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          position: 'relative',
        }}
      >
        <DataGridPro
          rows={rows}
          columns={columns}
          getRowId={handleGetRowId}
          loading={showLoader}
          density="compact"
          getRowHeight={handleGetRowHeight}
          getRowClassName={handleGetRowClassName}
          onRowsScrollEnd={handleOnRowsScrollEnd}
          onRowClick={handleOnRowClick}
          scrollEndThreshold={300}
          slots={gridSlots}
          slotProps={gridSlotProps}
          disableRowSelectionOnClick={true}
          disableColumnFilter={true}
          disableColumnSelector={true}
          disableColumnMenu={true}
          disableColumnReorder={true}
          hideFooter={true}
          localeText={gridLocaleText}
          sx={gridSx}
        />

        <LogDetailsModal
          open={Boolean(detailsRow)}
          onClose={() => setDetailsRow(null)}
          row={detailsRow}
          showDriver={showDriver}
          labels={labels}
          siteTimeZone={siteTimeZone}
        />
      </Box>
    );
  },
);
LogViewer.displayName = 'LogViewer';

/**
 * --------------------------------------------------------
 * Custom Datagrid Slots - Row, Cell
 * Namespaced `customProps` to avoid collision with mui props.
 * --------------------------------------------------------
 */

interface LogViewerGridRowProps extends GridRowProps {
  customProps?: {};
}

/**
 * Row wrapper: clicking anywhere in the row opens the details modal.
 * */
export const LogViewerGridRow = memo(
  forwardRef<HTMLDivElement, LogViewerGridRowProps>(
    ({ customProps, ...muiRowProps }, ref) => {
      return (
        <GridRow
          data-testid="log-viewer-row"
          ref={ref}
          {...muiRowProps}
        />
      );
    },
  ),
);
LogViewerGridRow.displayName = 'LogViewerGridRow';

interface LogViewerGridCellProps extends GridCellProps {
  customProps?: {
    [key: string]: any;
  };
}

export const LogViewerGridCell = memo(
  forwardRef<HTMLDivElement, LogViewerGridCellProps>(
    ({ customProps, ...muiCellProps }, ref) => {
      return (
        <GridCell
          ref={ref}
          {...muiCellProps}
        />
      );
    },
  ),
);
LogViewerGridCell.displayName = 'LogViewerGridCell';
